import React, { useState, useRef, useEffect, ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

interface NavDropdownProps {
  trigger: string;
  children: ReactNode;
  align?: 'left' | 'right' | 'center';
  width?: string;
  className?: string;
}

const NavDropdown: React.FC<NavDropdownProps> = ({
  trigger,
  children,
  align = 'left',
  width = 'w-64',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Close dropdown on Escape key and cleanup timeout
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, [isOpen]);

  // Close dropdown when clicking any link inside
  const handleLinkClick = () => {
    setIsOpen(false);
  };

  // Handle mouse enter - cancel any pending close and open immediately
  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsOpen(true);
  };

  // Handle mouse leave - delay close to allow moving to dropdown
  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 100); // 100ms delay
  };

  const alignmentClass = {
    left: 'left-0',
    right: 'right-0',
    center: 'left-1/2 -translate-x-1/2',
  }[align];

  return (
    <div
      className={`relative ${className}`}
      ref={dropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Dropdown Trigger Button */}
      <button
        className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-accent-600 dark:hover:text-accent-400 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors"
        aria-label={`${trigger} menu`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span>{trigger}</span>
        <ChevronDown
          size={16}
          className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Invisible bridge to prevent gap issues */}
          <div className="absolute left-0 right-0 h-2 pointer-events-none" aria-hidden="true" />

          <div
            className={`absolute ${alignmentClass} mt-2 ${width} bg-white dark:bg-neutral-800 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-700 py-2 z-50 animate-fadeIn`}
            role="menu"
            onClick={handleLinkClick}
          >
            {children}
          </div>
        </>
      )}
    </div>
  );
};

export default React.memo(NavDropdown);
